import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/errors";
import { Field, Input, Modal, PrimaryButton, QuietButton } from "../UI";

const EMPTY = { name: "", subject: "", body: "" };

/** LT-38 — create / edit an email template. Merge-field chips insert `{field}` at the cursor. */
export function EmailTemplateDialog({ open, onClose, initial, fields, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocus = useRef("body");

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(initial ? { name: initial.name || "", subject: initial.subject || "", body: initial.body || "" } : EMPTY);
  }, [open, initial]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const insert = (key) => {
    const tag = `{${key}}`;
    const el = lastFocus.current === "subject" ? subjectRef.current : bodyRef.current;
    const k = lastFocus.current === "subject" ? "subject" : "body";
    const cur = form[k] || "";
    const start = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + tag + cur.slice(end);
    setForm((f) => ({ ...f, [k]: next }));
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + tag.length, start + tag.length); } });
  };

  const save = async () => {
    if (!form.name.trim()) { setError("Give the template a name."); return; }
    setSaving(true); setError(null);
    try {
      const r = initial ? await api.patch(`/email-templates/${initial.id}`, form) : await api.post("/email-templates", form);
      toast.success(initial ? "Template saved" : "Template created");
      onSaved?.(r.data); onClose();
    } catch (e) { setError(formatApiError(e, "Could not save the template.")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit email template" : "New email template"} testid="email-template-dialog" maxWidth={720}
      footer={<>
        <QuietButton onClick={onClose} data-testid="email-template-dialog-cancel">Cancel</QuietButton>
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : undefined} data-testid="email-template-dialog-save">{initial ? "Save" : "Create template"}</PrimaryButton>
      </>}>
      <div className="grid gap-4">
        <Field label="Name" required>
          <Input value={form.name} onChange={set("name")} placeholder="e.g. Request for quote" data-testid="email-template-dialog-name" />
        </Field>
        <Field label="Subject">
          <input ref={subjectRef} value={form.subject} onChange={(e) => set("subject")(e.target.value)} onFocus={() => { lastFocus.current = "subject"; }}
            placeholder="Quote request — {job_type} at {site_address}" data-testid="email-template-dialog-subject"
            className="h-9 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent" style={{ fontSize: 13 }} />
        </Field>
        <Field label="Body" hint="Plain text — Outlook receives it via mailto, so no formatting or attachments. Click a field to insert it where the cursor is.">
          <textarea ref={bodyRef} value={form.body} onChange={(e) => set("body")(e.target.value)} onFocus={() => { lastFocus.current = "body"; }} rows={10} data-testid="email-template-dialog-body"
            className="px-3 py-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-y font-mono" style={{ fontSize: 12.5, lineHeight: "18px" }} />
        </Field>
        <div className="flex flex-wrap gap-1.5" data-testid="email-template-dialog-fields">
          {(fields || []).map((f) => (
            <button key={f.key} type="button" onClick={() => insert(f.key)} title={`e.g. ${f.example}`} data-testid={`email-template-field-${f.key}`}
              className="rounded-full px-2.5 py-0.5 bg-surface-2 text-ink-2 border border-line hover:border-accent hover:text-accent font-mono" style={{ fontSize: 11 }}>
              {`{${f.key}}`}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="mt-4 rounded-md border px-3 py-2" style={{ background: "#FBECEC", borderColor: "#E9C0C0", color: "#7A1F1F", fontSize: 12 }} data-testid="email-template-dialog-error">{error}</div>}
    </Modal>
  );
}
